<template>
  <div class="tracking-page min-h-screen bg-gray-50">
    <!-- Header -->
    <TheHeader />

    <!-- Main Content -->
    <main class="section-padding">
      <div class="container-custom">
        <!-- Title -->
        <div class="text-center mb-12 animate-fade-in-up">
          <h1 class="text-4xl md:text-5xl font-bold text-navy-500 mb-4">
            {{ $t('tracking.title') }}
          </h1>
          <p class="text-gray-600 text-lg">
            {{ $t('tracking.subtitle') }}
          </p>
        </div>

        <!-- Tracking Number Input -->
        <div class="max-w-2xl mx-auto mb-12 animate-fade-in-up delay-100">
          <div class="card p-8">
            <label for="trackingNumber" class="block text-sm font-medium text-navy-500 mb-3">
              {{ $t('tracking.form.label') }}
            </label>
            <div class="flex gap-3">
              <input
                id="trackingNumber"
                v-model="trackingNumber"
                type="text"
                :placeholder="$t('tracking.form.placeholder')"
                class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all uppercase"
                @keyup.enter="handleTrack"
              />
              <button
                @click="handleTrack"
                :disabled="!trackingNumber || isLoading"
                class="btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!isLoading">{{ $t('tracking.form.button') }}</span>
                <span v-else class="flex items-center gap-2">
                  <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ $t('tracking.form.loading') }}
                </span>
              </button>
            </div>

            <!-- Error message -->
            <p v-if="error" class="mt-3 text-sm text-red-600">
              {{ error }}
            </p>
          </div>
        </div>

        <!-- Not Found State -->
        <div v-if="notFound && !isLoading" class="max-w-2xl mx-auto mb-12 animate-fade-in-up">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <font-awesome-icon icon="box" class="text-4xl text-red-300 mb-3" />
            <p class="font-medium text-red-700 mb-2">{{ $t('tracking.not_found.title') }}</p>
            <p class="text-sm text-red-600">{{ $t('tracking.not_found.description') }}</p>
          </div>
        </div>

        <!-- Tracking Results (datos reales) -->
        <div v-if="data && !notFound" class="animate-fade-in-up delay-200">
          <div class="grid lg:grid-cols-3 gap-8">
            <!-- Left Column: Map and Info -->
            <div class="lg:col-span-2 space-y-6">
              <!-- Map / Driver Position -->
              <div class="card overflow-hidden">
                <div class="relative h-96 bg-gradient-to-br from-gray-100 to-gray-200">
                  <!-- Static Map Image (origen/destino y/o conductor) -->
                  <img
                    v-if="hasMapCoordinates && !mapLoadError"
                    :src="staticMapUrl"
                    alt="Mapa de ubicación"
                    class="absolute inset-0 w-full h-full object-cover"
                    crossorigin="anonymous"
                    referrerpolicy="no-referrer"
                    @error="mapLoadError = true"
                  />

                  <!-- Fallback: Animated mockup (sin coordenadas o error de carga) -->
                  <div v-if="!hasMapCoordinates || mapLoadError" class="absolute inset-0 flex items-center justify-center">
                    <div class="text-center space-y-4">
                      <div class="relative inline-block">
                        <div class="absolute inset-0 animate-ping">
                          <div class="w-16 h-16 rounded-full bg-primary/30"></div>
                        </div>
                        <div class="relative w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                          <font-awesome-icon icon="motorcycle" class="text-2xl text-white animate-float" />
                        </div>
                      </div>
                      <div class="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 inline-block shadow-lg">
                        <p class="text-sm text-gray-600">
                          {{ data.driver_position ? $t('tracking.map.location') : $t('tracking.map.waiting') }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Driver info overlay -->
                  <div v-if="data.driver" class="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-xs">
                    <div class="flex items-center gap-3">
                      <div class="w-12 h-12 rounded-full gradient-corporate flex items-center justify-center">
                        <font-awesome-icon icon="motorcycle" class="text-white text-xl" />
                      </div>
                      <div>
                        <p class="font-bold text-navy-500">{{ data.driver.name }}</p>
                        <p class="text-sm text-gray-600">{{ getServiceLabel(data.service_type) }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Status Badge -->
                  <div class="absolute top-4 right-4 rounded-lg shadow-lg px-4 py-2" :class="statusBadgeClass">
                    <p class="text-xs opacity-80">{{ $t('tracking.map.status') }}</p>
                    <p class="text-lg font-bold">{{ statusLabel }}</p>
                  </div>
                </div>
              </div>

              <!-- Delivery Details -->
              <div class="card p-6">
                <h3 class="text-xl font-bold text-navy-500 mb-4">
                  {{ $t('tracking.details.title') }}
                </h3>
                <div class="grid md:grid-cols-2 gap-4">
                  <div>
                    <p class="text-sm text-gray-600 mb-1">{{ $t('tracking.details.origin') }}</p>
                    <p class="font-medium text-navy-500">{{ data.origin?.comuna || '-' }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-600 mb-1">{{ $t('tracking.details.destination') }}</p>
                    <p class="font-medium text-navy-500">{{ data.destination?.comuna || '-' }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-600 mb-1">{{ $t('tracking.details.service') }}</p>
                    <p class="font-medium text-navy-500">{{ getServiceLabel(data.service_type) }}</p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-600 mb-1">{{ $t('tracking.details.tracking') }}</p>
                    <p class="font-mono font-medium text-primary">{{ data.track_code }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Timeline -->
            <div class="lg:col-span-1">
              <div class="card p-6 sticky top-24">
                <h3 class="text-xl font-bold text-navy-500 mb-6">
                  {{ $t('tracking.timeline.title') }}
                </h3>

                <!-- Timeline -->
                <div class="space-y-6">
                  <div
                    v-for="(step, index) in data.timeline"
                    :key="index"
                    class="relative pl-8"
                  >
                    <!-- Timeline line -->
                    <div
                      v-if="index < data.timeline.length - 1"
                      class="absolute left-2.5 top-8 w-0.5 h-full bg-primary"
                    ></div>

                    <!-- Timeline dot -->
                    <div
                      class="absolute left-0 top-1 w-5 h-5 rounded-full border-2 bg-primary border-primary"
                      :class="{ 'animate-pulse ring-2 ring-primary/40': index === data.timeline.length - 1 && data.is_active }"
                    >
                      <font-awesome-icon
                        icon="check"
                        class="text-white text-xs absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                      />
                    </div>

                    <!-- Step content -->
                    <div>
                      <p class="font-medium text-navy-500">
                        {{ step.label }}
                      </p>
                      <p class="text-sm text-gray-600">{{ formatTimestamp(step.at) }}</p>
                    </div>
                  </div>
                </div>

                <!-- Contact Driver (WhatsApp) -->
                <div class="mt-8 pt-6 border-t border-gray-200">
                  <a
                    href="https://wa.me/56992345248"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-xl" />
                    {{ $t('tracking.contact.driver') }}
                  </a>
                </div>

                <!-- Auto-refresh indicator -->
                <div v-if="data.is_active" class="mt-4 text-center">
                  <p class="text-xs text-gray-400 flex items-center justify-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                    {{ $t('tracking.auto_refresh') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State (sin búsqueda) -->
        <div v-if="!data && !notFound && !error" class="text-center py-16 animate-fade-in-up delay-200">
          <div class="w-32 h-32 mx-auto mb-6 text-gray-300">
            <font-awesome-icon icon="box" class="text-9xl" />
          </div>
          <p class="text-gray-600 text-lg">
            {{ $t('tracking.empty') }}
          </p>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <TheFooter />
  </div>
</template>

<script setup lang="ts">
import TheFooter from '@/components/layout/TheFooter.vue';
import TheHeader from '@/components/layout/TheHeader.vue';
import { useTracking } from '@/composables/useTracking';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

const {
    trackingData: data,
    isLoading,
    error,
    notFound,
    fetchTracking,
    getServiceLabel,
    formatTimestamp,
} = useTracking();

const trackingNumber = ref('');
const mapLoadError = ref(false);

// Status labels en español
const STATUS_LABELS: Record<string, string> = {
    unassigned: 'Sin asignar',
    assigned: 'Asignado',
    accepted: 'En camino',
    picked_up: 'Retirado',
    in_transit: 'En ruta',
    delivered: 'Entregado',
    delivery_failed: 'Fallido',
    returned: 'Devuelto',
    cancelled: 'Cancelado',
    pending_payment: 'Pago pendiente',
    requested: 'Solicitado',
    driver_arrived: 'En punto',
    in_progress: 'En progreso',
    completed: 'Completado',
};

const statusLabel = computed(() => {
    if (!data.value) return '';
    return STATUS_LABELS[data.value.status] || data.value.status;
});

const statusBadgeClass = computed(() => {
    if (!data.value) return 'bg-gray-500 text-white';
    const status = data.value.status;
    if (['delivered', 'completed'].includes(status)) return 'bg-green-500 text-white';
    if (['in_transit', 'picked_up', 'in_progress'].includes(status)) return 'bg-primary text-white';
    if (['delivery_failed', 'cancelled', 'returned'].includes(status)) return 'bg-red-500 text-white';
    return 'bg-amber-500 text-white';
});

const hasMapCoordinates = computed(() => {
    if (!data.value) return false;
    return !!(data.value.driver_position || data.value.origin?.lat || data.value.destination?.lat);
});

const staticMapUrl = computed(() => {
    if (!data.value) return '';
    const gmKey = import.meta.env.VITE_GOOGLE_MAPS_KEY || 'AIzaSyAvhCPS1LevYy-xSOxaRiPcn-dLaGvwBJo';
    const markers: string[] = [];

    // Marcador de origen (verde)
    if (data.value.origin?.lat && data.value.origin?.lng) {
        markers.push(`markers=color:green%7Clabel:O%7C${data.value.origin.lat},${data.value.origin.lng}`);
    }
    // Marcador de destino (rojo)
    if (data.value.destination?.lat && data.value.destination?.lng) {
        markers.push(`markers=color:red%7Clabel:D%7C${data.value.destination.lat},${data.value.destination.lng}`);
    }
    // Marcador del conductor (naranja)
    if (data.value.driver_position) {
        markers.push(`markers=color:orange%7Clabel:C%7C${data.value.driver_position.latitude},${data.value.driver_position.longitude}`);
    }

    if (markers.length === 0) return '';

    // Si hay conductor, centrar en él; si no, dejar que Google auto-centre con los markers
    const baseParams = `size=800x400&scale=2&maptype=roadmap&key=${gmKey}`;
    if (markers.length === 1) {
        // Un solo punto: zoom 14
        return `https://maps.googleapis.com/maps/api/staticmap?${baseParams}&zoom=14&${markers[0]}`;
    }
    // Múltiples puntos: Google auto-ajusta zoom
    return `https://maps.googleapis.com/maps/api/staticmap?${baseParams}&${markers.join('&')}`;
});

const handleTrack = async () => {
    if (!trackingNumber.value) return;
    mapLoadError.value = false;

    const success = await fetchTracking(trackingNumber.value);

    // Actualizar URL con el track code
    if (success && trackingNumber.value) {
        router.replace({ params: { trackCode: trackingNumber.value } });
    }
};

// Auto-buscar si viene con trackCode en la URL (path o query param)
onMounted(() => {
    const queryCode = Array.isArray(route.query.code) ? route.query.code[0] : route.query.code;
    const trackCode = (route.params.trackCode as string) || (queryCode as string);
    if (trackCode) {
        const normalizedCode = trackCode.toUpperCase();
        trackingNumber.value = normalizedCode;
        fetchTracking(normalizedCode).then((success) => {
            // Normalizar URL a path-based si venía por query param
            if (success && route.query.code) {
                router.replace({ params: { trackCode: normalizedCode }, query: {} });
            }
        });
    }
});
</script>

<style scoped>
@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
